import { GlButton, GlModal, GlFormTextarea, GlIcon, GlForm, GlFormInput, GlFormGroup, GlFormRadio, GlAlert, GlLink, GlFormCharacterCount, GlSprintf, GlToastMixin } from '../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import TimeAgoTooltip from '../../../vue_shared/components/time_ago_tooltip.vue?vue3';
import { __, s__, n__, sprintf } from '~/locale';
import { SAVED_VIEW_VISIBILITY, ROUTES } from '~/work_items/constants';
import { saveSavedView } from '../../../../../../ee/app/assets/javascripts/work_items/list/utils.js?vue3';
import { getIdFromGraphQLId } from '~/graphql_shared/utils';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import { helpPagePath } from '~/helpers/help_page_helper';
export default {
    name: 'WorkItemsNewSavedViewModal',
    components: {
        GlIcon,
        GlForm,
        GlFormInput,
        GlFormGroup,
        GlFormRadio,
        GlFormTextarea,
        GlButton,
        GlModal,
        GlAlert,
        GlLink,
        GlFormCharacterCount,
        GlSprintf,
        TimeAgoTooltip
    },
    mixins: [
        GlToastMixin
    ],
    i18n: {
        validateTitle: s__('WorkItem|Title is required.'),
        privateView: s__('WorkItem|Only you can see and edit this view.'),
        subscriptionLimitWarningMessage: s__('WorkItem|You have reached the maximum number of views in your list. If you add a view, the last view in your list will be removed.'),
        learnMoreAboutViewLimits: s__('WorkItem|Learn more about view limits.'),
        authorInfoCreatedBy: s__('WorkItem|Created by %{author}'),
        authorInfoUpdatedBy: s__('WorkItem|Updated %{timeAgo} by %{updatedBy}')
    },
    savedViewLimitsHelpPath: helpPagePath('user/work_items/saved_views.md', {
        anchor: 'saved-view-limits'
    }),
    inject: [
        'subscribedSavedViewLimit',
        'isGroup'
    ],
    model: {
        prop: 'show',
        event: 'hide'
    },
    props: {
        show: {
            type: Boolean,
            required: true
        },
        savedView: {
            type: Object,
            required: false,
            default: ()=>({})
        },
        fullPath: {
            type: String,
            required: true
        },
        filters: {
            type: Object,
            required: false,
            default: ()=>{}
        },
        displaySettings: {
            type: Object,
            required: false,
            default: ()=>{}
        },
        sortKey: {
            type: String,
            required: true
        },
        showSubscriptionLimitWarning: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'hide'
    ],
    MAX_TITLE_LENGTH: 40,
    MAX_DESCRIPTION_LENGTH: 140,
    SAVED_VIEW_VISIBILITY,
    data () {
        return {
            savedViewDescription: this.savedView?.description,
            savedViewTitle: this.savedView?.name,
            isTitleValid: true,
            isDescriptionValid: true,
            savedViewVisibility: this.getSavedViewVisibility(),
            error: '',
            showWarningOnOpen: false
        };
    },
    computed: {
        savedViewAuthor () {
            return this.savedView?.author ?? null;
        },
        savedViewAuthorId () {
            return this.savedViewAuthor ? this.getUserId(this.savedViewAuthor) : '';
        },
        savedViewLastUpdatedBy () {
            return this.savedView?.lastUpdatedBy ?? null;
        },
        savedViewLastUpdatedById () {
            return this.savedViewLastUpdatedBy ? this.getUserId(this.savedViewLastUpdatedBy) : '';
        },
        savedViewUpdatedAt () {
            return this.savedView?.updatedAt ?? '';
        },
        showAuthorInfo () {
            return this.isEdit && Boolean(this.savedViewAuthorId) && !this.savedView?.isPrivate;
        },
        modalTitle () {
            return this.isEdit ? s__('WorkItem|Edit view') : s__('WorkItem|New view');
        },
        submitButtonLabel () {
            return this.isEdit ? s__('WorkItem|Save') : s__('WorkItem|Create view');
        },
        isEdit () {
            return Boolean(this.savedView?.id);
        },
        canUpdateSavedViewVisibility () {
            return !this.isEdit || this.savedView?.userPermissions?.updateSavedViewVisibility;
        },
        visibilityText () {
            return sprintf(s__('WorkItem|Anyone with access to this %{namespaceType} can add the view, and those with the Planner and above roles can edit it.'), {
                namespaceType: this.isGroup ? __('group') : __('project')
            });
        },
        emptyTitleFeedback () {
            return this.savedViewTitle ? '' : this.$options.i18n.validateTitle;
        }
    },
    watch: {
        show: {
            immediate: true,
            handler (newValue) {
                this.savedViewTitle = this.savedView?.name;
                this.savedViewDescription = this.savedView?.description;
                this.savedViewVisibility = this.getSavedViewVisibility();
                if (newValue) {
                    this.showWarningOnOpen = this.showSubscriptionLimitWarning;
                }
            }
        },
        savedViewTitle (newTitle, oldTitle) {
            if (newTitle && newTitle !== oldTitle) {
                this.validateTitle();
            }
        },
        savedViewDescription () {
            this.validateDescription();
        }
    },
    methods: {
        getUserId (user) {
            return getIdFromGraphQLId(user.id);
        },
        focusTitleInput () {
            this.$refs.savedViewTitle?.$el.focus();
        },
        validateTitle () {
            const trimmedTitle = this.savedViewTitle?.trim() ?? '';
            this.isTitleValid = trimmedTitle.length > 0 && trimmedTitle.length <= this.$options.MAX_TITLE_LENGTH;
        },
        async saveView () {
            this.validateTitle();
            this.validateDescription();
            if (!this.isTitleValid || !this.isDescriptionValid) {
                return;
            }
            const mutationKey = this.isEdit ? 'workItemSavedViewUpdate' : 'workItemSavedViewCreate';
            try {
                const { data } = await saveSavedView({
                    isEdit: this.isEdit,
                    isForm: true,
                    namespacePath: this.fullPath,
                    id: this.savedView?.id,
                    name: this.savedViewTitle,
                    description: this.savedViewDescription,
                    isPrivate: this.savedViewVisibility === SAVED_VIEW_VISIBILITY.PRIVATE,
                    filters: this.filters ?? {},
                    displaySettings: this.displaySettings,
                    sort: this.sortKey,
                    userPermissions: this.savedView?.userPermissions,
                    subscribed: this.savedView?.subscribed,
                    mutationKey,
                    apolloClient: this.$apollo,
                    subscribedSavedViewLimit: this.subscribedSavedViewLimit
                });
                if (data[mutationKey].errors?.length) {
                    const fallback = this.isEdit ? s__('WorkItem|Something went wrong while saving the view') : s__('WorkItem|Something went wrong while creating the view');
                    this.error = data[mutationKey].errors[0] || fallback;
                    return;
                }
                if (!this.isEdit) {
                    const newViewId = getIdFromGraphQLId(data[mutationKey].savedView.id);
                    this.$router.push({
                        name: ROUTES.savedView,
                        params: {
                            view_id: newViewId.toString()
                        },
                        query: undefined
                    });
                }
                this.$toast.show(this.isEdit ? s__('WorkItem|View has been saved.') : s__('WorkItem|New view created.'));
                this.hideAddNewViewModal();
            } catch (e) {
                Sentry.captureException(e);
                this.error = this.isEdit ? s__('WorkItem|Something went wrong while saving the view') : s__('WorkItem|Something went wrong while creating the view');
            }
        },
        validateDescription () {
            this.isDescriptionValid = (this.savedViewDescription?.length ?? 0) <= this.$options.MAX_DESCRIPTION_LENGTH;
        },
        resetModal () {
            this.isTitleValid = true;
            this.isDescriptionValid = true;
            this.savedViewTitle = '';
            this.savedViewDescription = '';
            this.savedViewVisibility = SAVED_VIEW_VISIBILITY.PRIVATE;
        },
        hideAddNewViewModal () {
            this.resetModal();
            this.$emit('hide', false);
        },
        getSavedViewVisibility () {
            if (!this.savedView?.id) {
                return SAVED_VIEW_VISIBILITY.PRIVATE;
            }
            return this.savedView?.isPrivate ? SAVED_VIEW_VISIBILITY.PRIVATE : SAVED_VIEW_VISIBILITY.SHARED;
        },
        overLimitText (count) {
            return n__('%d character over limit.', '%d characters over limit.', count);
        }
    }
};
